import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { IonicModule } from '@ionic/angular';
import { HttpErrorResponse, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, Subject, filter, firstValueFrom, takeUntil, tap } from 'rxjs';
import { CustomerService } from '../../../core/services/customer.service';
import { SpatialService } from '../../../core/services/spatial.service';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { SpatialAssetRole, SpatialBindingsRequest, SpatialEditor, SpatialInstallation, SpatialInstallationEditor, SpatialIssue, SpatialMutation, SpatialPortalMutation, SpatialProfileRequest, SpatialRegistrationDevice, SpatialRegistrationEditor, SpatialRegistrationSensor, SpatialScope, SpatialUnitAttestationRequest, spatialBlockingMessage } from '../../../shared/models/be/spatial';
import { sameSpatialProfile, suggestSpatialBindings } from './spatial-registration.utils';
import { createUuid } from '../../../shared/utils/uuid.util';

interface PendingWrite {
  draftId?: string; installationId: string; revision: string | null; etag: string; key: string;
}
type PortalOperation =
  { kind: 'create'; installationId: string; key: string; } |
  { kind: 'profile'; installationId: string; key: string; etag: string; body: SpatialProfileRequest; } |
  { kind: 'producer'; installationId: string; key: string; etag: string; reference: string; } |
  { kind: 'bindings'; installationId: string; key: string; etag: string; draftId: string; body: SpatialBindingsRequest; } |
  { kind: 'attestation'; installationId: string; key: string; etag: string; draftId: string; body: SpatialUnitAttestationRequest; };

@Component({
  selector: 'app-spatial-editor', standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatCheckboxModule, MatIconModule, IonicModule],
  templateUrl: './spatial-editor.component.html', styleUrls: ['./spatial-editor.component.scss']
})
export class SpatialEditorComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) scope!: SpatialScope;
  @Input() unitName = '';
  private readonly api = inject(SpatialService);
  private readonly customers = inject(CustomerService);
  private readonly requests = inject(RequestsService);
  private readonly cancel$ = new Subject<void>();
  private destroyed = false;
  private generation = 0;
  private etag = '';
  private pendingCreate?: PendingWrite;
  private pendingSave?: PendingWrite;
  private pendingPortal?: PortalOperation;
  private pendingPortalUserId?: number;
  private installationETag = '';
  private technicalFresh = false;
  public enabled = false;
  public busy = false;
  public editor: SpatialEditor | null = null;
  public installations: SpatialInstallation[] = [];
  public installationId = '';
  public files: Partial<Record<SpatialAssetRole, File>> = {};
  public progress: Partial<Record<SpatialAssetRole, number>> = {};
  public draggingRole: SpatialAssetRole | null = null;
  public feedback = '';
  public error = '';
  public showCreateInstallation = false;
  public newInstallationId = '';
  public technicalOpen = false;
  public installationEditor: SpatialInstallationEditor | null = null;
  public registration: SpatialRegistrationEditor | null = null;
  public bindingSelections: Record<string, string> = Object.create(null);
  public producerReference = '';
  public packageReference = '';
  public producerConfirmed = false;
  public packageConfirmed = false;
  public coverageReferences: Record<string, string> = Object.create(null);
  public coverageConfirmed: Record<string, boolean> = Object.create(null);
  public technicalErrors: SpatialIssue[] = [];
  public blockedUnitIds: string[] = [];
  public unitNames: Record<string, string> = Object.create(null);
  public unitLabel(id: string): string { return id === this.scope.unitId ? this.unitName || 'Questa unità' : this.unitNames[id] || id; }
  public readonly roles: { role: SpatialAssetRole; label: string; accept: string; hint: string; }[] = [
    { role: 'image', label: 'Immagine della mappa', accept: '.png,image/png', hint: 'PNG · massimo 10 MiB' },
    { role: 'map_descriptor', label: 'Descrittore della mappa', accept: '.json,application/json', hint: 'JSON · massimo 1 MiB' },
    { role: 'tracking_manifest', label: 'Configurazione sensori e tracking', accept: '.json,.zip,application/json,application/zip', hint: 'JSON (1 MiB) oppure ZIP YAML (10 MiB)' }
  ];
  public readonly stateMessage = spatialBlockingMessage;
  public get visible(): boolean { return this.enabled && this.requests.getCurrentUser()?.role === 'superuser'; }
  public get retryPending(): boolean { return !!(this.pendingCreate || this.pendingSave || this.pendingPortal); }
  public get portalRetryPending(): boolean { return !!this.pendingPortal; }
  public get canSave(): boolean { return this.visible && !this.busy && !this.pendingPortal && !!this.editor && !!this.installationId && (!!this.etag || !!this.pendingCreate || !!this.pendingSave); }
  public get canPrepare(): boolean { return this.canSave && !this.pendingSave; }
  public get hasSelectedFiles(): boolean { return this.roles.some(field => !!this.files[field.role]); }
  public get canSelectFiles(): boolean { return this.visible && !!this.editor && !this.busy && !this.retryPending && !!this.installationId; }
  public get validInstallationId(): boolean { return /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,254}$/.test(this.newInstallationId); }
  public get canCreateInstallation(): boolean { return this.visible && !this.busy && !this.retryPending && !this.installationLocked && this.validInstallationId; }
  public get profileMatches(): boolean { return !!this.installationEditor && sameSpatialProfile(this.installationEditor.profile, this.registration?.candidateProfile || null); }
  public get enabledSensors(): SpatialRegistrationSensor[] { return this.registration?.sensors.filter(sensor => sensor.enabled) || []; }
  public get technicalPackageValid(): boolean { return !!this.registration?.packageDigest && this.roles.every(field => !!this.registration!.assets[field.role]); }
  public get canEditTechnical(): boolean { return this.visible && !this.busy && !this.retryPending && this.technicalFresh && !this.hasSelectedFiles && !!this.etag && !!this.installationETag && this.technicalPackageValid; }
  public get commonChangesBlocked(): boolean { return !!this.installationEditor?.blockingActiveUnitIds.length; }
  public get localChangesBlocked(): boolean { return !!this.registration?.blockingActiveUnitIds.length; }
  public get bindingsDirty(): boolean {
    return this.enabledSensors.some(sensor => (this.bindingSelections[sensor.sensorId] || '') !== (sensor.assignedUnitId === this.scope.unitId ? sensor.deviceId || '' : ''));
  }
  public get canAdoptProfile(): boolean { return this.canEditTechnical && this.registration?.candidateProfile?.installation_id === this.installationId && !this.profileMatches && !this.commonChangesBlocked; }
  public get canAttestProducer(): boolean {
    return this.canEditTechnical && this.registration?.producerStatus !== 'VERIFIED' && this.profileMatches && !this.commonChangesBlocked && this.producerConfirmed && this.validReference(this.producerReference);
  }
  public get canAttestPackage(): boolean {
    return this.canEditTechnical && this.registration?.packageStatus !== 'VERIFIED' && !this.registration!.validationErrors.length && this.profileMatches && this.allBindingsSaved && !this.bindingsDirty && !this.localChangesBlocked
      && this.packageConfirmed && this.validReference(this.packageReference);
  }
  public get canAttestCoverage(): boolean {
    return this.canEditTechnical && !this.registration!.validationErrors.length && this.profileMatches && this.allBindingsSaved && !this.bindingsDirty && !this.localChangesBlocked
      && this.coverageToAttest.length > 0 && this.coverageToAttest.every(entry => this.validReference(entry.reference));
  }
  public get canSaveBindings(): boolean {
    const ids = this.enabledSensors.map(sensor => this.bindingSelections[sensor.sensorId]).filter(Boolean);
    return this.canEditTechnical && this.profileMatches && !this.localChangesBlocked && new Set(ids).size === ids.length
      && this.enabledSensors.every(sensor => !this.bindingSelections[sensor.sensorId]
        || this.registration!.devices.some(device => device.deviceId === this.bindingSelections[sensor.sensorId] && this.deviceAllowed(sensor, device)));
  }
  public get allBindingsSaved(): boolean { return this.enabledSensors.length > 0 && this.enabledSensors.every(sensor => !!sensor.deviceId && sensor.assignedUnitId === this.scope.unitId); }
  public get coverageToAttest(): { sensorId: string; reference: string; }[] {
    return this.enabledSensors.filter(sensor => sensor.coverageStatus !== 'VERIFIED' && this.coverageConfirmed[sensor.sensorId])
      .map(sensor => ({ sensorId: sensor.sensorId, reference: (this.coverageReferences[sensor.sensorId] || '').trim() }));
  }
  public validReference(value: string): boolean { return !!value.trim() && value.trim().length <= 2000; }
  public deviceAllowed(sensor: SpatialRegistrationSensor, device: SpatialRegistrationDevice): boolean {
    return (!sensor.assignedUnitId || sensor.assignedUnitId === this.scope.unitId) && (device.available || device.deviceId === sensor.deviceId)
      && (!sensor.expectedDeviceLabel || sensor.expectedDeviceLabel === device.deviceLabel)
      && !this.enabledSensors.some(other => other.sensorId !== sensor.sensorId && this.bindingSelections[other.sensorId] === device.deviceId);
  }
  public get revision() { return this.editor?.draft || this.editor?.active || this.editor?.retained || null; }
  public get installationLocked(): boolean { return !!(this.editor?.draft || this.editor?.active || this.editor?.retained); }

  ngOnChanges(): void {
    this.cancel$.next(); this.generation++;
    this.editor = null; this.installations = []; this.files = {}; this.progress = {}; this.etag = ''; this.draggingRole = null;
    this.pendingCreate = undefined; this.pendingSave = undefined; this.installationId = '';
    this.pendingPortal = undefined; this.newInstallationId = ''; this.showCreateInstallation = false; this.resetTechnical();
    this.unitNames = Object.create(null);
    this.enabled = false; this.feedback = ''; this.error = '';
    if (this.requests.getCurrentUser()?.role === 'superuser') void this.initialize(this.generation);
  }
  ngOnDestroy(): void { this.destroyed = true; this.generation++; this.cancel$.next(); this.cancel$.complete(); }
  private wait<T>(operation: Observable<T>): Promise<T> { return firstValueFrom(operation.pipe(takeUntil(this.cancel$))); }
  private assertCurrent(generation: number): void {
    if (generation !== this.generation || this.destroyed || !this.visible) throw new Error('Contesto Spatial non più disponibile.');
  }
  private async initialize(generation: number): Promise<void> {
    this.busy = true;
    try {
      const customer = await this.wait(this.customers.getCustomer$(String(this.scope.customerId)));
      if (generation !== this.generation || this.destroyed || this.requests.getCurrentUser()?.role !== 'superuser') return;
      this.enabled = customer.spatialTrackingEnabled === true;
      if (this.enabled) {
        await this.reloadInstallations(generation);
        if (generation !== this.generation || this.destroyed) return;
        await this.reload();
      }
    } catch (error) { if (generation === this.generation) this.error = this.errorMessage(error); }
    finally { if (generation === this.generation) this.busy = false; }
  }
  private remember(response: HttpResponse<unknown>): void {
    const etag = response.headers.get('ETag');
    if (!etag) {
      this.etag = '';
      throw new Error('Versione della configurazione non disponibile. Ricaricare prima di salvare.');
    }
    this.etag = etag;
  }
  public async reload(): Promise<void> {
    const generation = this.generation;
    this.etag = '';
    const response = await this.wait(this.api.editor(this.scope));
    if (generation !== this.generation || this.destroyed) return;
    this.remember(response); this.editor = response.body!;
    this.installationId = this.editor.selectedInstallationId || this.installationId;
    if (!this.editor.draft) this.resetTechnical();
  }
  private async reloadInstallations(generation: number): Promise<void> {
    const catalog = await this.wait(this.api.installations(this.scope));
    if (generation !== this.generation || this.destroyed) return;
    this.installations = catalog.installations;
  }
  public async refresh(): Promise<void> {
    if (this.busy || !this.visible) return;
    const generation = this.generation;
    this.busy = true; this.error = '';
    try {
      await this.reloadInstallations(generation);
      if (generation !== this.generation || this.destroyed) return;
      await this.reload();
      if (this.technicalOpen) await this.loadTechnical();
    } catch (error) { if (generation === this.generation && !this.destroyed) this.error = this.errorMessage(error); }
    finally { if (generation === this.generation) this.busy = false; }
  }
  public selectFile(role: SpatialAssetRole, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0]; input.value = '';
    if (!file) return;
    this.queueFile(role, file);
  }
  public dragOver(role: SpatialAssetRole, event: DragEvent): void {
    event.preventDefault(); event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = this.canSelectFiles ? 'copy' : 'none';
    this.draggingRole = this.canSelectFiles ? role : null;
  }
  public dragLeave(role: SpatialAssetRole, event: DragEvent): void {
    event.preventDefault(); event.stopPropagation();
    const target = event.currentTarget as HTMLElement | null;
    if (event.relatedTarget instanceof Node && target?.contains(event.relatedTarget)) return;
    if (this.draggingRole === role) this.draggingRole = null;
  }
  public dropFile(role: SpatialAssetRole, event: DragEvent): void {
    event.preventDefault(); event.stopPropagation(); this.draggingRole = null;
    if (!this.canSelectFiles) return;
    const files = event.dataTransfer?.files;
    if (!files?.length) return;
    if (files.length !== 1) { this.error = 'Caricare un solo file per campo.'; return; }
    this.queueFile(role, files[0]);
  }
  private queueFile(role: SpatialAssetRole, file: File): void {
    if (!this.canSelectFiles) return;
    const extension = file.name.toLowerCase().split('.').pop();
    const allowed = role === 'image' ? ['png'] : role === 'map_descriptor' ? ['json'] : ['json', 'zip'];
    if (!allowed.includes(extension || '')) {
      this.error = 'Formato non consentito. ' + this.roles.find(field => field.role === role)!.hint + '.';
      return;
    }
    const limit = role === 'map_descriptor' || (role === 'tracking_manifest' && !file.name.toLowerCase().endsWith('.zip')) ? 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > limit) { this.error = 'Il file supera il limite previsto per questo campo.'; return; }
    this.files = { ...this.files, [role]: file }; this.error = ''; this.feedback = '';
    this.producerConfirmed = false; this.packageConfirmed = false; this.coverageConfirmed = Object.create(null);
  }
  public installationSelectionChanged(): void { this.resetTechnical(); this.feedback = ''; this.error = ''; }
  private resetTechnical(): void {
    this.technicalOpen = false; this.technicalFresh = false; this.installationETag = '';
    this.installationEditor = null; this.registration = null; this.bindingSelections = Object.create(null);
    this.producerReference = ''; this.packageReference = ''; this.producerConfirmed = false; this.packageConfirmed = false;
    this.coverageReferences = Object.create(null); this.coverageConfirmed = Object.create(null);
    this.technicalErrors = []; this.blockedUnitIds = [];
  }
  private async loadTechnical(): Promise<void> {
    this.technicalFresh = false;
    const generation = this.generation, draft = this.editor?.draft;
    if (!draft || !this.visible) return;
    let installation = await this.wait(this.api.installationEditor(this.scope, draft.installation_id));
    this.assertCurrent(generation);
    const response = await this.wait(this.api.registration(this.scope, draft.revisionId));
    if (generation !== this.generation || this.destroyed) return;
    const registration = response.body!;
    if (registration.customerId !== this.scope.customerId || registration.buildingId !== this.scope.buildingId || registration.unitId !== this.scope.unitId
      || registration.draftId !== draft.revisionId || registration.installation_id !== draft.installation_id) throw new Error('Registrazione non corrispondente all’unità selezionata.');
    if (installation.body?.installationVersion !== registration.installationVersion) {
      installation = await this.wait(this.api.installationEditor(this.scope, draft.installation_id));
    }
    if (generation !== this.generation || this.destroyed) return;
    const state = installation.body!;
    if (state.installation_id !== draft.installation_id || state.installationVersion !== registration.installationVersion
      || state.installationETag !== registration.installationETag || installation.headers.get('ETag') !== state.installationETag
      || (response.headers.has('X-Spatial-Installation-ETag') && response.headers.get('X-Spatial-Installation-ETag') !== state.installationETag)) {
      throw new Error('L’installazione è cambiata. Aggiornare lo stato prima di procedere.');
    }
    this.remember(response); this.installationETag = state.installationETag;
    if (this.registration && (this.registration.packageDigest !== registration.packageDigest || !sameSpatialProfile(this.registration.profile, registration.profile))) {
      this.packageConfirmed = false; this.producerConfirmed = false; this.coverageConfirmed = Object.create(null);
    }
    this.installationEditor = state; this.registration = registration;
    this.editor = { ...this.editor!, draftVersion: registration.draftVersion };
    this.bindingSelections = suggestSpatialBindings(registration);
    this.technicalErrors = []; this.blockedUnitIds = [];
    if (state.affectedUnitIds.some(id => id !== this.scope.unitId && !this.unitNames[id])) {
      try {
        const list = await this.wait(this.api.units(this.scope));
        this.assertCurrent(generation);
        for (const unit of list.units) {
          if (unit.id && unit.customerId === this.scope.customerId && unit.buildingId === this.scope.buildingId) this.unitNames[unit.id] = unit.name;
        }
      } catch { /* Unit IDs remain available if names cannot be resolved. */ }
    }
    this.assertCurrent(generation);
    this.technicalFresh = true;
  }
  public async createInstallation(): Promise<void> {
    if (!this.canCreateInstallation) return;
    await this.runPortal({ kind: 'create', installationId: this.newInstallationId, key: createUuid() });
  }
  public async adoptProfile(): Promise<void> {
    if (!this.canAdoptProfile || !this.registration) return;
    if (this.installationEditor?.profileStatus === 'CONFIGURED' && !window.confirm('Aggiornare il profilo comune dell’installazione? Le verifiche delle unità coinvolte dovranno essere completate nuovamente.')) return;
    await this.runPortal({ kind: 'profile', installationId: this.installationId, etag: this.installationETag, key: createUuid(),
      body: { unitId: this.scope.unitId, draftId: this.registration.draftId, draftVersion: this.registration.draftVersion } });
  }
  public async saveBindings(): Promise<void> {
    if (!this.canSaveBindings || !this.registration) return;
    await this.runPortal({ kind: 'bindings', installationId: this.installationId, etag: this.etag, key: createUuid(), draftId: this.registration.draftId,
      body: { installationVersion: this.registration.installationVersion,
        bindings: this.enabledSensors.filter(sensor => !!this.bindingSelections[sensor.sensorId]).map(sensor => ({ sensorId: sensor.sensorId, deviceId: this.bindingSelections[sensor.sensorId] })) } });
  }
  public async attestProducer(): Promise<void> {
    if (!this.canAttestProducer) return;
    await this.runPortal({ kind: 'producer', installationId: this.installationId, etag: this.installationETag, key: createUuid(), reference: this.producerReference.trim() });
  }
  public async attestPackage(): Promise<void> {
    if (!this.canAttestPackage || !this.registration) return;
    await this.runPortal({ kind: 'attestation', installationId: this.installationId, draftId: this.registration.draftId, etag: this.etag, key: createUuid(),
      body: { installationVersion: this.registration.installationVersion, kind: 'PACKAGE', reference: this.packageReference.trim() } });
  }
  public async attestCoverage(): Promise<void> {
    if (!this.canAttestCoverage || !this.registration) return;
    await this.runPortal({ kind: 'attestation', installationId: this.installationId, draftId: this.registration.draftId, etag: this.etag, key: createUuid(),
      body: { installationVersion: this.registration.installationVersion, kind: 'COVERAGE', references: this.coverageToAttest } });
  }
  public async retryPortal(): Promise<void> {
    if (this.pendingPortal && !this.busy && this.visible) await this.runPortal(this.pendingPortal);
  }
  private portalRequest(operation: PortalOperation): Observable<HttpResponse<SpatialPortalMutation>> {
    switch (operation.kind) {
      case 'create': return this.api.createInstallation(this.scope, operation.installationId, operation.key);
      case 'profile': return this.api.adoptProfile(this.scope, operation.installationId, operation.body, operation.etag, operation.key);
      case 'producer': return this.api.attestProducer(this.scope, operation.installationId, operation.reference, operation.etag, operation.key);
      case 'bindings': return this.api.saveBindings(this.scope, operation.draftId, operation.body, operation.etag, operation.key);
      case 'attestation': return this.api.attestUnit(this.scope, operation.draftId, operation.body, operation.etag, operation.key);
    }
  }
  private async runPortal(operation: PortalOperation): Promise<void> {
    if (this.busy || !this.visible) return;
    if (this.pendingPortal && this.pendingPortalUserId !== this.requests.getCurrentUser()?.id) {
      this.pendingPortal = undefined; this.technicalFresh = false; this.error = 'L’utente è cambiato. Aggiornare lo stato prima di una nuova operazione.'; return;
    }
    if (!this.pendingPortal) this.pendingPortalUserId = this.requests.getCurrentUser()?.id;
    this.busy = true; this.error = ''; this.feedback = ''; this.pendingPortal = operation;
    const generation = this.generation;
    let applied = false;
    try {
      const response = await this.wait(this.portalRequest(operation));
      this.assertCurrent(generation);
      this.pendingPortal = undefined; applied = true;
      if (operation.kind === 'create') {
        this.installationId = operation.installationId; this.newInstallationId = ''; this.showCreateInstallation = false;
        this.feedback = 'Installazione creata. Caricare i tre file e preparare le verifiche tecniche.';
      } else {
        this.feedback = 'Operazione tecnica completata. L’attivazione avviene con Salva configurazione.';
        if (operation.kind === 'producer') { this.producerConfirmed = false; this.producerReference = ''; }
        if (operation.kind === 'attestation' && operation.body.kind === 'PACKAGE') { this.packageConfirmed = false; this.packageReference = ''; }
        if (operation.kind === 'attestation' && operation.body.kind === 'COVERAGE') { this.coverageConfirmed = Object.create(null); this.coverageReferences = Object.create(null); }
      }
      if (operation.kind === 'bindings' || operation.kind === 'attestation') this.remember(response);
      this.installationETag = response.headers.get(operation.kind === 'bindings' || operation.kind === 'attestation' ? 'X-Spatial-Installation-ETag' : 'ETag')
        || response.body?.installationETag || '';
      await this.reloadInstallations(generation); this.assertCurrent(generation);
      await this.reload(); this.assertCurrent(generation);
      if (this.technicalOpen) await this.loadTechnical();
      else if (operation.kind === 'create') {
        const editor = await this.wait(this.api.installationEditor(this.scope, operation.installationId));
        if (generation === this.generation && !this.destroyed) this.installationEditor = editor.body;
      }
    } catch (error) {
      if (generation !== this.generation || this.destroyed) return;
      if (!(error instanceof HttpErrorResponse) || error.status !== 0 || applied) this.pendingPortal = undefined;
      this.technicalFresh = false;
      this.error = applied ? 'Operazione completata, ma lo stato non è stato ricaricato. Premere Aggiorna stato.'
        : this.errorMessage(error);
      const details = error instanceof HttpErrorResponse ? error.error : null;
      const issues = [...(details?.missingRequirements || []), ...(details?.validationErrors || [])] as SpatialIssue[];
      const blocked = Array.isArray(details?.blockingActiveUnitIds) ? details.blockingActiveUnitIds : [];
      try { await this.reload(); if (this.technicalOpen) await this.loadTechnical(); } catch { /* The original operation remains recoverable. */ }
      this.technicalErrors = issues; this.blockedUnitIds = blocked;
    } finally { if (generation === this.generation) this.busy = false; }
  }
  private async prepareDraft(): Promise<void> {
    const generation = this.generation;
    if (!this.editor) throw new Error('Ricaricare la configurazione.');
    if (this.pendingCreate || !this.editor.draft) {
      this.pendingCreate ||= { installationId: this.installationId, revision: this.editor.activeConfigRevisionId, etag: this.etag, key: createUuid() };
      const operation = this.pendingCreate;
      const response = await this.wait(this.api.createDraft(this.scope, operation.installationId, operation.revision, operation.etag, operation.key));
      this.assertCurrent(generation);
      this.remember(response); this.pendingCreate = undefined;
      await this.reload();
      this.assertCurrent(generation);
    }
    const draft = this.editor?.draft;
    if (!draft) throw new Error('Bozza non disponibile. Ricaricare la configurazione.');
    for (const { role } of this.roles) {
      const file = this.files[role]; if (!file) continue;
      this.technicalFresh = false; this.progress[role] = 0;
      const response = await this.wait(this.api.upload(this.scope, draft.revisionId, role, file, this.etag).pipe(
        tap(event => {
          if (event.type === HttpEventType.UploadProgress) this.progress[role] = event.total ? Math.round(event.loaded / event.total * 100) : 0;
        }),
        filter((event): event is HttpResponse<SpatialMutation> => event instanceof HttpResponse)
      ));
      this.assertCurrent(generation);
      this.remember(response); delete this.files[role]; this.progress[role] = 100;
    }
  }
  public async prepareRegistration(): Promise<void> {
    if (!this.canPrepare) return;
    this.busy = true; this.error = ''; this.feedback = '';
    const generation = this.generation;
    try {
      await this.prepareDraft(); this.assertCurrent(generation); await this.reload();
      this.assertCurrent(generation);
      this.technicalOpen = true; await this.loadTechnical();
      this.feedback = 'File della bozza disponibili. Completare associazioni e verifiche, poi premere Salva configurazione.';
    } catch (error) { await this.configurationError(error, generation); }
    finally { if (generation === this.generation) this.busy = false; }
  }
  public async save(): Promise<void> {
    if (!this.canSave || !this.editor) return;
    this.busy = true; this.error = ''; this.feedback = '';
    const generation = this.generation;
    try {
      if (!this.pendingSave) {
        await this.prepareDraft();
        this.assertCurrent(generation);
        const draft = this.editor!.draft!;
        this.pendingSave = { draftId: draft.revisionId, installationId: draft.installation_id, revision: draft.baseActiveRevisionId, etag: this.etag, key: createUuid() };
      }
      const operation = this.pendingSave;
      const response = await this.wait(this.api.save(this.scope, operation.draftId!, operation.revision, operation.etag, operation.key));
      this.assertCurrent(generation);
      this.remember(response); this.pendingSave = undefined;
      this.feedback = response.body?.outcome === 'CONFIGURATION_ACTIVATED'
        ? 'Configurazione attivata. La mappa è disponibile in Mentorage.'
        : 'Bozza salvata. Completare i requisiti indicati per attivare la mappa.';
      await this.reload();
      if (this.technicalOpen) await this.loadTechnical();
    } catch (error) { await this.configurationError(error, generation); }
    finally { if (generation === this.generation) this.busy = false; }
  }
  private async configurationError(error: unknown, generation: number): Promise<void> {
    if (generation !== this.generation || this.destroyed) return;
    if (!(error instanceof HttpErrorResponse) || error.status !== 0) { this.pendingCreate = undefined; this.pendingSave = undefined; }
    if (error instanceof HttpErrorResponse) this.etag = error.headers.get('ETag') || this.etag;
    this.error = this.errorMessage(error);
    try { await this.reload(); if (this.technicalOpen) await this.loadTechnical(); } catch { /* Refresh before further writes. */ }
  }
  public async action(kind: 'discard' | 'deactivate' | 'remove' | 'asset', role?: SpatialAssetRole): Promise<void> {
    if (this.busy || this.retryPending || !this.editor || !this.etag) return;
    if (kind !== 'asset' && !window.confirm(kind === 'remove' ? 'Rimuovere la configurazione Spatial e la mappa di questa unità?' : kind === 'deactivate' ? 'Disattivare lo Spatial Tracking di questa unità?' : 'Abbandonare la bozza e i suoi caricamenti?')) return;
    this.busy = true; this.error = ''; this.feedback = '';
    const generation = this.generation;
    try {
      const draft = this.editor.draft;
      const request = kind === 'asset' && draft && role ? this.api.removeAsset(this.scope, draft.revisionId, role, this.etag)
        : kind === 'discard' && draft ? this.api.discard(this.scope, draft.revisionId, this.etag)
        : kind === 'deactivate' ? this.api.deactivate(this.scope, this.editor.activeConfigRevisionId, this.etag)
        : kind === 'remove' ? this.api.removeConfiguration(this.scope, this.editor.activeConfigRevisionId, this.etag) : null;
      if (!request) return;
      const response = await this.wait(request);
      this.assertCurrent(generation); this.remember(response);
      if (role) delete this.files[role]; else if (kind !== 'deactivate') this.files = {};
      if (kind === 'remove') this.installationId = '';
      await this.reload();
      if (this.technicalOpen) await this.loadTechnical();
    } catch (error) {
      if (generation !== this.generation || this.destroyed) return;
      this.error = this.errorMessage(error); try { await this.reload(); } catch { /* Retry via refresh. */ }
    }
    finally { if (generation === this.generation) this.busy = false; }
  }
  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 403) { this.enabled = false; return 'Accesso Spatial non disponibile per questo cliente.'; }
      const code = error.error?.error?.code;
      if (code === 'INSTALLATION_ID_COLLISION') return 'Questo ID installazione è già registrato. Selezionarlo dal catalogo se disponibile oppure verificare l’ID del producer.';
      if (code === 'ACTIVE_CONFIGURATIONS_BLOCK_TECHNICAL_CHANGE') return 'Disattivare le unità indicate prima di modificare la registrazione tecnica. Nessuna unità è stata disattivata automaticamente.';
      if (code === 'BINDING_ALREADY_ASSIGNED') return 'Sensore o dispositivo già associato a un’altra unità. Correggere il pacchetto o le associazioni.';
      if ([409, 412].includes(error.status)) return 'La configurazione è cambiata. Rivedere i dati aggiornati prima di riprovare.';
      if (error.status === 422) return 'Configurazione non valida: correggere gli errori indicati.'
        + (this.editor?.active ? ' La configurazione attiva precedente è conservata.' : '');
      if (error.status === 0) return this.portalRetryPending ? 'Risposta non ricevuta. Premere Riprova operazione per recuperare lo stesso comando.'
        : this.retryPending ? 'Risposta non ricevuta. Riprova Salva configurazione per recuperare la stessa operazione.' : 'Connessione non disponibile. Ricaricare lo stato prima di riprovare.';
      return error.error?.error?.message || 'Operazione non completata. Ricaricare lo stato della configurazione.';
    }
    return error instanceof Error ? error.message : 'Configurazione non disponibile.';
  }
}
